import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "./lib";

export const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div className={className} initial={reduce ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

export const SampleTag = ({ show, className = "" }) =>
  show ? (
    <span data-testid="sample-tag" className={`inline-block bg-lou-ochre/90 text-white text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${className}`}>
      Sample
    </span>
  ) : null;

export const PageHeader = ({ eyebrow, title, intro, testid }) => (
  <header className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 sm:pt-24 pb-10" data-testid={testid}>
    <Reveal>
      {eyebrow && <p className="text-xs tracking-[0.28em] uppercase text-lou-clay mb-5">{eyebrow}</p>}
      <h1 className="font-lou text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] max-w-3xl">{title}</h1>
      {intro && <p className="mt-6 text-base sm:text-lg text-lou-soft max-w-2xl leading-relaxed">{intro}</p>}
    </Reveal>
  </header>
);

export const Empty = ({ children, testid }) => (
  <p data-testid={testid} className="font-lou italic text-lg text-lou-soft py-12 border-t border-lou-line">{children}</p>
);

export const ArtImage = ({ src, alt, className = "", testid }) =>
  src ? (
    <img src={src} alt={alt} loading="lazy" data-testid={testid}
      className={`w-full h-auto block bg-lou-line/40 ${className}`} />
  ) : (
    <div className={`w-full aspect-[4/5] bg-lou-line/50 flex items-center justify-center text-lou-soft text-sm font-lou italic ${className}`}>
      Image to be added
    </div>
  );

export const Field = ({ label, id, textarea, hint, ...p }) => (
  <div>
    <label htmlFor={id} className="lou-label">{label}</label>
    {textarea ? <textarea id={id} className="lou-input min-h-[140px]" {...p} /> : <input id={id} className="lou-input" {...p} />}
    {hint && <p className="text-xs text-lou-soft mt-1.5">{hint}</p>}
  </div>
);

export function useHuman() {
  const [ch, setCh] = useState(null);
  const [answer, setAnswer] = useState("");
  const [website, setWebsite] = useState("");
  const load = () => api.get("/challenge").then((r) => { setCh(r.data); setAnswer(""); });
  useEffect(() => { load(); }, []);
  const values = { challenge_token: ch?.token || "", challenge_answer: answer, website };
  const ui = (
    <div className="grid sm:grid-cols-2 gap-4 items-end">
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
      </div>
      <Field id="human-check" label={ch ? `A quick check: ${ch.question}` : "Loading a quick check…"} inputMode="numeric"
        required value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="human-check-input" />
    </div>
  );
  return { values, ui, reset: load };
}

export function Lightbox({ items, index, onClose, onIndex }) {
  const closeRef = useRef(null);
  const reduce = useReducedMotion();
  const open = index !== null && index !== undefined;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    closeRef.current?.focus();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % items.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; prev?.focus?.(); };
  }, [open, index, items.length, onClose, onIndex]);
  const it = open ? items[index] : null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div role="dialog" aria-modal="true" aria-label={it?.title || "Image viewer"} data-testid="lightbox"
          className="fixed inset-0 z-[1000] bg-[#1d1a17]/95 text-[#f7f1e8] flex flex-col" style={{ fontFamily: "'Figtree', system-ui, sans-serif" }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
          initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="flex items-center justify-between px-4 sm:px-8 py-4">
            <p className="text-sm text-lou-paper/70">{index + 1} / {items.length}</p>
            <button ref={closeRef} onClick={onClose} data-testid="lightbox-close" aria-label="Close viewer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm transition-colors">
              <X size={22} /> Close
            </button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16 relative"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            {items.length > 1 && (
              <button onClick={() => onIndex((index - 1 + items.length) % items.length)} aria-label="Previous image"
                data-testid="lightbox-prev" className="absolute left-1 sm:left-4 p-3 hover:text-white z-10"><ChevronLeft size={32} /></button>
            )}
            {it.video ? (
              <video src={it.src} controls className="max-h-full max-w-full" />
            ) : (
              <img src={it.src} alt={it.title || ""} className="max-h-full max-w-full object-contain" data-testid="lightbox-image" />
            )}
            {items.length > 1 && (
              <button onClick={() => onIndex((index + 1) % items.length)} aria-label="Next image"
                data-testid="lightbox-next" className="absolute right-1 sm:right-4 p-3 hover:text-white z-10"><ChevronRight size={32} /></button>
            )}
          </div>
          <div className="px-6 py-5 text-center max-w-3xl mx-auto w-full" onClick={onClose}>
            {it.title && <p className="font-lou text-xl">{it.title}</p>}
            {it.caption && <p className="text-sm text-lou-paper/70 mt-1">{it.caption}</p>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export const Notice = ({ kind = "ok", children, testid }) => (
  <div role={kind === "error" ? "alert" : "status"} data-testid={testid}
    className={`px-4 py-3 text-sm border ${kind === "error" ? "border-red-300 bg-red-50 text-red-800" : "border-lou-sage/40 bg-lou-sage/10 text-lou-ink"}`}>
    {children}
  </div>
);
